import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation } from 'react-query';
import { toast } from 'utils/toast';
import { FAVORITES_MESSAGES } from 'locales/en-US';
import { api } from 'api/api';
import { logEvent } from 'firebase/analytics';
import { getAnalyticsInstance } from 'firebase.js';
import { useTeamColors } from 'hooks';
import {
  favoriteTeamsError,
  favoriteTeamsSuccess,
} from 'redux/favoriteTeams/actionCreators.js';

import { Star } from './style.js';

/**
 * AddToFavorite component allows users to add/remove teams from their favorites list.
 * @component
 * @param {Object} props - Component props
 * @param {string} props.userId - The ID of the currently logged in user
 * @returns {React.ReactElement} A star icon button that toggles favorite status
 * @example
 * ```jsx
 * <AddToFavorite userId="123" />
 * ```
 */
export const AddToFavorite = ({ userId }) => {
  const analytics = getAnalyticsInstance();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const params = Object.fromEntries([...searchParams]);
  const favoriteTeams = useSelector((state) => state.favoriteTeams.data);
  const { mainColor } = useTeamColors();

  // Check if favorites feature is enabled - handle both string '1' and integer 1
  const configData = useSelector((state) => state.configuration?.data);
  const isFavoritesEnabled =
    configData?.user_favorites_enabled === 1 ||
    configData?.user_favorites_enabled === '1';

  //** GET ALL FAVORITE TEAMS FOR LOGIN USER **//
  const getAllFavoriteTeams = useMutation(
    (id) => api.favorites.getFavoritesTeams(id),
    {
      onError: () => {
        dispatch(favoriteTeamsError());
        logEvent(analytics, 'select_content', {
          content_type: 'error',
          content_id: `Favorite teams fetching error for user - ${userId}. Team ${params?.team}, teamId ${params?.teamId} and site url: ${window.location.href}`,
        });
      },
      onSuccess: (data) => {
        dispatch(favoriteTeamsSuccess(data.data));
      },
    }
  );

  const [favTeam, setFavTeam] = useState(false);

  const addToFavoriteTeams = useMutation(
    (data) => api.favorites.addToFavoriteTeams(data),
    {
      onError: () => {
        toast.error(FAVORITES_MESSAGES.addError(params?.team));

        logEvent(analytics, 'select_content', {
          content_type: 'error',
          content_id: `Adding to favorites list error. User with userId ${userId}. Team ${params?.team}, teamId ${params?.teamId} and site url: ${window.location.href}`,
        });
      },
      onSuccess: () => {
        setFavTeam(true);
        toast.success(FAVORITES_MESSAGES.added(params?.team));
        getAllFavoriteTeams.mutate(userId);
      },
    }
  );

  const deleteFavoriteTeam = useMutation(
    (data) => api.favorites.deleteFavoriteTeam(data),
    {
      onError: () => {
        toast.error(FAVORITES_MESSAGES.removeError(params?.team));

        logEvent(analytics, 'select_content', {
          content_type: 'error',
          content_id: `Deleting from favorites list error. User with userId ${userId}. Team ${params?.team}, teamId ${params?.teamId} and site url: ${window.location.href}`,
        });
      },
      onSuccess: () => {
        setFavTeam(false);
        toast.success(FAVORITES_MESSAGES.removed(params?.team));
        getAllFavoriteTeams.mutate(userId);
      },
    }
  );

  const isDisabled =
    !!params?.team &&
    !!params?.teamId &&
    !!params?.league &&
    !!params?.leagueId;

  const handleFavoriteClick = useCallback(() => {
    const { teamId, team, league, leagueId } = params;

    if (userId && favTeam) {
      const data = {
        teamId,
        userId,
      };
      deleteFavoriteTeam.mutate(data);

      logEvent(analytics, 'select_content', {
        content_type: 'Delete team from favorite',
        content_id: team,
      });

      return;
    }

    if (userId && teamId && team) {
      addToFavoriteTeams.mutate({
        userId,
        teamId: teamId,
        teamLabel: team,
        league: league,
        leagueId: leagueId,
      });

      logEvent(analytics, 'select_content', {
        content_type: 'Add team to favorite',
        content_id: team,
      });
    }
  }, [
    addToFavoriteTeams,
    analytics,
    deleteFavoriteTeam,
    favTeam,
    params,
    userId,
  ]);

  useEffect(() => {
    const isTeamFavorite = favoriteTeams?.filter(
      (team) =>
        team.favourite_teamLabel === params?.team &&
        team.favourite_team_id === Number(params?.teamId)
    );

    if (isTeamFavorite?.length) {
      setFavTeam(true);
    } else {
      setFavTeam(false);
    }
  }, [params.team, params?.teamId, favoriteTeams]);

  // Don't render if feature is disabled
  if (!isFavoritesEnabled) {
    return null;
  }

  return (
    <Star
      $activeColor={mainColor}
      $hoverColor={mainColor}
      $isDisabled={
        !isDisabled ||
        addToFavoriteTeams.isLoading ||
        deleteFavoriteTeam.isLoading
      }
      $isFavorite={favTeam}
      onClick={handleFavoriteClick}
    />
  );
};
